/* eslint-disable */
import React, { useEffect, useState, useCallback } from 'react'
import ForecastCard, { ForecastCardProps } from './ForecastCard'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  type CarouselApi,
} from '../../../common/Carousel/Carousel'
import type { ActivePlace } from './ForecastCardCarousel.state'
import { cn } from '@/lib/ui/utils'
import styles from './ForecastCardCarousel.module.css'
import { Skeleton } from '@/components/layout/Skeleton/Skeleton'
import ForecastCardCarouselSkeleton from '@/services/components/strategies/weather/carousel/WeatherCardCarousel/ForecastCardCarouselSkeleton'

export interface ForecastCardCarouselProps {
  cards: ActivePlace[]
  onActive?: (card: ActivePlace & ForecastCardProps, index: number) => void
}

const ForecastCardCarousel: React.FC<ForecastCardCarouselProps> = ({
  cards,
  onActive,
}) => {
  const [contentApi, setContentApi] = useState<CarouselApi | null>(null)
  const [controlsApi, setControlsApi] = useState<CarouselApi | null>(null)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const hasCards = Boolean(cards?.length)

  // keep selectedIndex in range when cards change
  useEffect(() => {
    if (!hasCards) return
    if (selectedIndex > cards.length - 1) {
      setSelectedIndex(cards.length - 1)
    }
  }, [hasCards, cards.length, selectedIndex])

  const syncFromContent = useCallback(() => {
    if (!contentApi || !hasCards) return
    const idx = contentApi.selectedScrollSnap()
    setSelectedIndex(idx)

    const place = cards[idx]
    if (place) {
      const first = place.limitedDayboxes?.[0]
      const fcProps: ForecastCardProps = {
        name: place.name,
        dayDescription: place.dayDescription,
        currentSymbol: place.currentSymbol,
        currentTemp: place.currentTemp,
        currentFeltTemp: place.currentFeltTemp,
        now: true,
        localTime: place.localTime,
        weekDay: first?.weekDay ?? '',
        tMax: Number(first?.attributes?.tMax ?? 0),
        tMin: Number(first?.attributes?.tMin ?? 0),
        limitedDayboxes: place.limitedDayboxes as any,
        url: place.url,
      }
      onActive?.({ ...(place as any), ...fcProps }, idx)
    }

    controlsApi?.scrollTo(idx)
  }, [contentApi, controlsApi, hasCards, cards, onActive])

  useEffect(() => {
    if (!contentApi || !hasCards) return
    contentApi.on('select', syncFromContent)
    // fire once on mount to emit active card
    syncFromContent()
    return () => {
      contentApi.off('select', syncFromContent)
    }
  }, [contentApi, hasCards, syncFromContent])

  const scrollTo = (idx: number) => {
    setSelectedIndex(idx)
    contentApi?.scrollTo(idx)
  }
  const canScrollNext = () => selectedIndex < cards.length - 1
  const canScrollPrev = () => selectedIndex > 0

  return (
    <div className="flex flex-col items-center space-y-4 h-full">
      <div className="relative w-full">
        <Carousel
          setApi={setContentApi}
          opts={{
            align: 'start',
            loop: false,
            skipSnaps: true,
            dragFree: false,
          }}
        >
          <CarouselContent className="w-full">
            {!hasCards && <ForecastCardCarouselSkeleton />}
            {cards?.map((place) => {
              const first = place.limitedDayboxes?.[0]
              const fcProps: ForecastCardProps = {
                name: place.name,
                dayDescription: place.dayDescription,
                currentSymbol: place.currentSymbol,
                currentTemp: place.currentTemp,
                currentFeltTemp: place.currentFeltTemp,
                now: true,
                localTime: place.localTime,
                weekDay: first?.weekDay ?? '',
                tMax: Number(first?.attributes?.tMax ?? 0),
                tMin: Number(first?.attributes?.tMin ?? 0),
                limitedDayboxes: place.limitedDayboxes as any,
                url: place.url,
              }
              return (
                <CarouselItem key={place.url} className="basis-full">
                  <div>
                    <ForecastCard {...fcProps} />
                  </div>
                </CarouselItem>
              )
            })}
          </CarouselContent>
        </Carousel>
      </div>

      {!hasCards && (
        <div className="flex pt-2 pb-2.5 gap-2">
          {[...Array(2)].map((_, idx) => (
            <Skeleton key={idx} className="px-2 h-5 w-20" />
          ))}
        </div>
      )}

      {hasCards && (
        <div className="relative w-full max-w-[260px] min-h-[32px]">
          {cards?.length <= 1 ? (
            <div aria-hidden className="flex justify-center py-2">
              <span className="px-2 h-5 inline-flex items-center rounded opacity-0">
                &nbsp;placeholder&nbsp;
              </span>
            </div>
          ) : (
            <Carousel
              setApi={setControlsApi}
              opts={{
                loop: false,
                dragFree: false,
                skipSnaps: true,
                containScroll: 'keepSnaps',
              }}
              className="py-2 flex items-center justify-center"
            >
              <div className="flex flex-row justify-center items-center">
                <CarouselPrevious
                  className={cn(
                    'z-10 cursor-pointer mr-2 transition-opacity',
                    !canScrollPrev() && 'opacity-40',
                  )}
                  onClick={() => canScrollPrev() && scrollTo(selectedIndex - 1)}
                />
                <div
                  className={`relative max-w-[200px] md:max-w-[300px] ${styles.controls}`}
                >
                  <CarouselContent className="px-2">
                    {cards?.map((place, idx) => (
                      <CarouselItem
                        key={place.url}
                        className="flex items-center justify-center"
                      >
                        <button
                          onClick={() => scrollTo(idx)}
                          className={cn(
                            'inline-block whitespace-nowrap text-sm transition-opacity max-w-24 truncate',
                            idx === selectedIndex ? 'font-bold' : 'text-black',
                          )}
                        >
                          {place.name}
                        </button>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </div>
                <CarouselNext
                  className={cn(
                    'z-10 cursor-pointer ml-2 transition-opacity',
                    !canScrollNext() && 'opacity-40',
                  )}
                  onClick={() => canScrollNext() && scrollTo(selectedIndex + 1)}
                />
              </div>
            </Carousel>
          )}
        </div>
      )}
    </div>
  )
}

export default ForecastCardCarousel
